import { Plane, Loader2 } from 'lucide-react'
import React from 'react'
import { formatShortDate } from '@/lib/date'
import type { ToolRendererProps } from './types'
import { ToolDetailsPopover } from './ToolDetailsPopover'

/**
 * Transportation search result data structure (matches Go backend Result)
 */
interface TransportationResult {
  summary: string
}

/**
 * Simple renderer for the "transportation_search" tool that displays
 * a concise summary of flight search results.
 *
 * Example: "SEA → BOS, Jan 15"
 */
export function TransportationToolRenderer({
  toolCall,
  toolResponse,
  isCompleted,
  copyText,
}: ToolRendererProps) {
  // Show loading state if not completed
  if (!isCompleted || !toolResponse) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-magazine-pearl rounded-lg">
        <Loader2 className="w-3.5 h-3.5 text-magazine-dust animate-spin" />
        <span className="font-sans text-caption text-magazine-ash">
          Searching for flights...
        </span>
      </div>
    )
  }

  // Handle errors
  if (toolResponse.error) {
    return (
      <div className="px-3 py-1.5 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Error: {toolResponse.error}
        </p>
      </div>
    )
  }

  // Parse the tool response content
  let result: TransportationResult
  try {
    result = JSON.parse(toolResponse.content || '{}')
  } catch (error) {
    console.error('Failed to parse transportation tool response:', error)
    return (
      <div className="px-3 py-1.5 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Failed to parse flight results
        </p>
      </div>
    )
  }

  // Parse arguments to extract route info for compact display
  let departure = ''
  let arrival = ''
  let departureDate = ''
  try {
    const args = JSON.parse(toolCall.arguments || '{}')
    departure = args.departure || ''
    arrival = args.arrival || ''
    departureDate = args.departureDate || ''
  } catch (error) {
    // If we can't parse args, just show the summary
  }

  // Create compact route display
  const routeDisplay = departure && arrival
    ? `${departure} → ${arrival}${departureDate ? ', ' + formatShortDate(departureDate) : ''}`
    : 'Flight search'

  return (
    <ToolDetailsPopover
      toolCall={toolCall}
      toolResponse={toolResponse}
      copyText={copyText}
    >
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-magazine-sky/10 border border-magazine-sky/20 rounded-lg cursor-pointer hover:bg-magazine-sky/15 transition-colors"
        title="Click to view details"
      >
        <Plane className="w-3.5 h-3.5 text-magazine-sky" />
        <span className="font-sans text-caption text-magazine-charcoal">
          {routeDisplay}
        </span>
      </div>
    </ToolDetailsPopover>
  )
}
